// Shared HABC-M subscale data used by both the full HABC Monitor flow
// (Components/HABCMonitor.js) and the in-chat SubscaleQuiz (Components/SubscaleQuiz.js).
// Question wording must stay in sync with the validated HABC-M CG instrument.

export const HABC_SECTIONS = [
  {
    title: 'Cognitive Subscale',
    color: '#f8d7da', // pink
    items: [
      'Over the past two weeks, how often did your loved one have problems with judgment or decision-making and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with repeating the same things over and over such as questions or stories and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with forgetting the correct month or year and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with handling complicated financial affairs such as balancing checkbook, income taxes & paying bills and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with remembering appointments and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with thinking or memory and caused you or your loved one stress?',
    ],
  },
  {
    title: 'Functional Subscale',
    color: '#fff3cd', // yellow
    items: [
      'Over the past two weeks, how often did your loved one have problems with learning how to use a tool, appliance, or gadget and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with planning, preparing, or serving meals and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with taking medications in the right dose at the right time and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with walking or physical ambulation and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with bathing and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with shopping for personal items like groceries and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with housework or household chores and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with leaving her/him alone and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with her/his safety and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with her/his quality of life and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with falling or tripping and caused you or your loved one stress?',
    ],
  },
  {
    title: 'Behavioral & Mood Subscale',
    color: '#d4edda', // green
    items: [
      'Over the past two weeks, how often did your loved one have problems with less interest or pleasure in doing things and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with feeling down, depressed, or hopeless and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with being stubborn, agitated, aggressive or resistive to help from others and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with feeling anxious, nervous, tense, fearful or panic and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with believing others are stealing from them or planning to harm them and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with hearing voices, seeing things or talking to people who are not there and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with poor appetite or overeating and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with falling asleep, staying asleep, or sleeping too much and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with acting impulsively, without thinking through the consequences of her/his actions and caused you or your loved one stress?',
      'Over the past two weeks, how often did your loved one have problems with wandering, pacing, or doing things repeatedly and caused you or your loved one stress?',
    ],
  },
  {
    title: 'Caregiver Stress Subscale',
    color: '#cfe2ff', // blue
    items: [
      'Over the past two weeks, how often did you have problems with your quality of life and caused you or your loved one stress?',
      'Over the past two weeks, how often did you have problems with your financial future and caused you or your loved one stress?',
      'Over the past two weeks, how often did you have problems with your mental health and caused you or your loved one stress?',
      'Over the past two weeks, how often did you have problems with your physical health and caused you or your loved one stress?',
    ],
  },
];

export const HABC_RESPONSE_OPTIONS = [
  { label: 'Not at all (0)', value: 0 },
  { label: 'Several days (1)', value: 1 },
  { label: 'More than half the days (2)', value: 2 },
  { label: 'Nearly every day (3)', value: 3 },
];

// Maps the backend classifier category to the HABC_SECTIONS index.
export const CATEGORY_TO_SECTION_INDEX = {
  cognitive: 0,
  functional: 1,
  behavioral: 2,
  caregiverStress: 3,
};

// Category used when the chat administers the whole monitor (all four
// subscales) instead of a single subscale. Mirrors HABC_FULL_CATEGORY in
// backend/app.js.
export const HABC_FULL_CATEGORY = 'habc';
export const HABC_TOTAL_MAX_SCORE = 93;

export const SUBSCALE_LABELS = {
  cognitive: 'Cognitive',
  functional: 'Functional',
  behavioral: 'Behavioral & Mood',
  caregiverStress: 'Caregiver Stress',
  [HABC_FULL_CATEGORY]: 'HABC Monitor',
};

// True for any category the in-chat quiz can administer.
export const isQuizCategory = (category) =>
  category === HABC_FULL_CATEGORY || CATEGORY_TO_SECTION_INDEX[category] != null;

// Flat, ordered question list for the in-chat quiz. For the full monitor it
// walks all four sections; for a subscale, just that section. Keys use the
// HABC Monitor format `sec{sectionIndex}_q{itemIndex}`.
export const getQuizQuestions = (category) => {
  const sectionIndexes = category === HABC_FULL_CATEGORY
    ? HABC_SECTIONS.map((_, index) => index)
    : [CATEGORY_TO_SECTION_INDEX[category]].filter((index) => index != null);

  return sectionIndexes.flatMap((sectionIndex) => {
    const section = HABC_SECTIONS[sectionIndex];
    const items = section?.items || [];
    return items.map((text, itemIndex) => ({
      key: `sec${sectionIndex}_q${itemIndex}`,
      text,
      sectionIndex,
      sectionTitle: section.title,
      sectionPosition: sectionIndexes.indexOf(sectionIndex) + 1,
      sectionCount: sectionIndexes.length,
      itemIndex,
      itemCount: items.length,
    }));
  });
};

// Answer key -> question text, so the backend can label answers without
// knowing the question order.
export const buildQuestionsByKey = (category) =>
  Object.fromEntries(getQuizQuestions(category).map((question) => [question.key, question.text]));
